// src/components/InstallPrompt.js
import React, { useState, useEffect } from 'react';

/**
 * Component that displays a prompt to install the PWA
 * This triggers when the browser's beforeinstallprompt event fires
 */
function InstallPrompt() {
  const [installPromptEvent, setInstallPromptEvent] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [userResponded, setUserResponded] = useState(false);

  useEffect(() => {
    // Check if user has already responded to the prompt (from localStorage)
    const hasRespondedToPWA = localStorage.getItem('pwa_install_responded');
    if (hasRespondedToPWA === 'true') {
      setUserResponded(true);
    }

    // Listen for the beforeinstallprompt event
    const handleBeforeInstallPrompt = (e) => {
      // Prevent Chrome 67 and earlier from automatically showing the prompt
      e.preventDefault();
      
      // Stash the event so it can be triggered later
      setInstallPromptEvent(e);
      
      // Only show our custom install prompt if user hasn't previously responded
      if (hasRespondedToPWA !== 'true') {
        // 5-second delay before showing the prompt for better user experience
        setTimeout(() => {
          setShowPrompt(true);
        }, 5000);
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Check if app is already installed
    window.addEventListener('appinstalled', () => {
      // The app was installed, hide the prompt
      setShowPrompt(false);
      
      // Mark that user has responded
      localStorage.setItem('pwa_install_responded', 'true');
      setUserResponded(true);
      
      console.log('Blue Comet was successfully installed');
    });

    // Clean up event listeners
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', () => {});
    };
  }, []);

  const handleInstallClick = () => {
    if (!installPromptEvent) return;

    // Show the install prompt
    installPromptEvent.prompt();

    // Wait for the user to respond to the prompt
    installPromptEvent.userChoice.then((choiceResult) => {
      // Mark that user has responded regardless of outcome
      localStorage.setItem('pwa_install_responded', 'true');
      setUserResponded(true);
      
      if (choiceResult.outcome === 'accepted') {
        console.log('User accepted the install prompt');
      } else {
        console.log('User dismissed the install prompt');
      }
      
      // Reset the installPromptEvent - it can only be used once
      setInstallPromptEvent(null);
      setShowPrompt(false);
    });
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    
    // Mark that user has responded
    localStorage.setItem('pwa_install_responded', 'true');
    setUserResponded(true);
  };

  // Don't render anything if we shouldn't show the prompt
  if (!showPrompt) return null;

  return (
    <div className="install-prompt" style={{
      position: 'fixed',
      bottom: '20px',
      left: '50%',
      transform: 'translateX(-50%)',
      background: 'white',
      padding: '16px 20px',
      borderRadius: '12px',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
      display: 'flex',
      alignItems: 'center',
      zIndex: 1000,
      maxWidth: '90%',
      width: '400px',
      animation: 'slideUpFade 0.3s ease-out forwards',
    }}>
      <div style={{ 
        marginRight: '16px', 
        fontSize: '32px',
        background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        🚀
      </div>
      <div style={{ flex: 1 }}>
        <p style={{ margin: '0 0 8px 0', fontWeight: '600', fontSize: '16px', color: '#2d3748' }}>
          Install Blue Comet
        </p>
        <p style={{ margin: '0', fontSize: '14px', color: '#718096' }}>
          Add to your home screen for faster access
        </p>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <button 
          onClick={handleInstallClick}
          style={{
            background: 'linear-gradient(135deg, #0078ff 0%, #0056b3 100%)',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            padding: '8px 16px',
            cursor: 'pointer',
            fontWeight: '500',
            fontSize: '14px',
          }}
        >
          Install
        </button>
        <button 
          onClick={handleDismiss}
          style={{
            background: 'none',
            border: '1px solid #e2e8f0',
            borderRadius: '6px',
            padding: '6px 12px',
            cursor: 'pointer', 
            fontSize: '14px',
            color: '#718096',
          }}
        >
          Not now
        </button>
      </div>
      
      {/* Global style for the animation */}
      <style>
        {`
          @keyframes slideUpFade {
            from { 
              opacity: 0;
              transform: translateY(20px) translateX(-50%);
            }
            to { 
              opacity: 1;
              transform: translateY(0) translateX(-50%);
            }
          }
        `}
      </style>
    </div>
  );
}

export default InstallPrompt;